import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface QuoteMetrics {
  totalQuotes: number;
  totalRevenue: number;
  paidRevenue: number;
  pendingRevenue: number;
  averageDealSize: number;
  conversionRate: number;
}

export const useQuoteMetrics = (salesRepId?: string | null, dateRange?: { start: Date; end: Date }) => {
  const [metrics, setMetrics] = useState<QuoteMetrics>({
    totalQuotes: 0,
    totalRevenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    averageDealSize: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('quotes')
        .select('total_amount, down_payment_status, balance_payment_status, created_at');

      if (salesRepId) {
        query = query.eq('sales_rep_id', salesRepId);
      }

      if (dateRange) {
        query = query
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString());
      }

      const { data: quotes, error } = await query;

      if (error) throw error;

      const totalQuotes = quotes?.length || 0;
      const totalRevenue = quotes?.reduce((sum, q) => sum + (Number(q.total_amount) || 0), 0) || 0;
      
      const paidQuotes = quotes?.filter(
        q => q.down_payment_status === 'paid' || q.balance_payment_status === 'paid'
      ) || [];
      
      const paidRevenue = paidQuotes.reduce((sum, q) => sum + (Number(q.total_amount) || 0), 0);
      const pendingRevenue = totalRevenue - paidRevenue;
      const averageDealSize = totalQuotes > 0 ? totalRevenue / totalQuotes : 0;
      const conversionRate = totalQuotes > 0 ? (paidQuotes.length / totalQuotes) * 100 : 0;

      setMetrics({
        totalQuotes,
        totalRevenue,
        paidRevenue,
        pendingRevenue,
        averageDealSize,
        conversionRate,
      });
    } catch (error) {
      console.error('Error fetching quote metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [salesRepId, dateRange]);

  return { metrics, loading, refetch: fetchMetrics };
};
